import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook encapsulating the drag-to-resize logic for Gantt phase bars.
 *
 * Exposes:
 *  - phaseDurations:        Map of phaseId → custom weeks (empty when at defaults)
 *  - setPhaseDurations:     Setter (use to reset, e.g. setPhaseDurations({}))
 *  - handleDragStart(e, id): Bind to onMouseDown / onTouchStart of the drag handle
 *  - isDragging:            Ref to the live dragging state (for transition tweaks)
 *  - trackRefs:             Ref-map used by phase row containers
 */
export function useGanttDrag({ basePhases, displayPhases, phaseDurations, setPhaseDurations }) {
  const isDragging = useRef(false);
  const dragData = useRef(null);
  const trackRefs = useRef({});

  const handleDragStart = useCallback((e, phaseId) => {
    e.preventDefault();
    const trackEl = trackRefs.current[phaseId];
    if (!trackEl) return;
    const rect = trackEl.getBoundingClientRect();
    isDragging.current = true;
    dragData.current = {
      phaseId,
      startX: e.clientX,
      startWeeks: phaseDurations[phaseId] ?? (basePhases.find(p => p.id === phaseId)?.durationWeeks ?? 4),
      trackWidth: rect.width,
      // Use current total at drag-start as the scale basis to avoid feedback loop
      scaleWeeks: displayPhases.reduce((sum, p) => sum + p.durationWeeks, 0),
    };
  }, [phaseDurations, basePhases, displayPhases]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current || !dragData.current) return;
      const { phaseId, startX, startWeeks, trackWidth, scaleWeeks } = dragData.current;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const dx = clientX - startX;
      const weeksPerPx = scaleWeeks / trackWidth;
      const newWeeks = Math.max(1, Math.round(startWeeks + dx * weeksPerPx));
      setPhaseDurations(prev => ({ ...prev, [phaseId]: newWeeks }));
    };
    const onUp = () => { isDragging.current = false; dragData.current = null; };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [setPhaseDurations]);

  return { isDragging, trackRefs, handleDragStart };
}
